/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/no-empty-function */
import { FormControl, FormErrorMessage, FormLabel, Text } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import _ from 'lodash';
import { Select, Props } from 'chakra-react-select';
import { colors } from '../../../../theme/colors';
import { extractErrorMessage } from '../../../../utils/ErrorUtils';

interface MultiSelectProps<TData> extends Omit<Props, 'onChange' | 'isMulti'> {
  name: string;
  label?: string;
  labelCN?: React.ComponentProps<typeof FormLabel>['className'];
  controlWidth?: number;
  controlMb?: number;
  maxSelectedOptions?: number;
  // eslint-disable-next-line no-unused-vars
  onValueChange?(props: TData[]): void;
  isRequired?: boolean;
}

function MultiSelectInputForm<TData>({
  name,
  label,
  labelCN,
  placeholder,
  controlWidth,
  controlMb,
  maxSelectedOptions = 4,
  onValueChange,
  options,
  isRequired,
  ...rest
}: MultiSelectProps<TData>) {
  const {
    formState: { errors },
    control,
  } = useFormContext();

  const error = _.get(errors, name) as any;

  return (
    <FormControl isRequired={isRequired} isInvalid={!!error} mb={controlMb ?? 5}>
      {label && (
        <FormLabel className={labelCN} fontWeight="bold" mb={1} mr={3} htmlFor={name}>
          {label}
        </FormLabel>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange, ref } }) => {
          const currentValue = value as TData[] | null | undefined;
          const selectedCount = currentValue?.length ?? 0;
          const isMaxReached = selectedCount >= maxSelectedOptions;

          // Filtra opções disponíveis quando o limite é atingido
          const availableOptions = useMemo(() => {
            if (!isMaxReached) return options;
            // Quando o limite é atingido, só mostra as opções já selecionadas
            const selectedValues = currentValue?.map((v: any) => v.value) ?? [];
            return (options as any[])?.filter((opt: any) => selectedValues.includes(opt.value));
          }, [options, isMaxReached, currentValue]);

          return (
            <>
              <Select
                isMulti
                options={availableOptions}
                ref={ref}
                onChange={newOptions => {
                  const newValue = newOptions as TData[];
                  // Limita a seleção ao máximo permitido
                  if (newValue.length <= maxSelectedOptions) {
                    onChange(newValue);
                    onValueChange && onValueChange(newValue);
                  }
                }}
                isClearable
                isSearchable
                value={currentValue}
                placeholder={placeholder ?? 'Selecione uma ou mais opções'}
                noOptionsMessage={() =>
                  isMaxReached
                    ? `Máximo de ${maxSelectedOptions} seleções atingido`
                    : 'Nenhum resultado encontrado'
                }
                loadingMessage={() => 'Carregando...'}
                chakraStyles={{
                  control: (styles, { isDisabled }) => ({
                    ...styles,
                    w: controlWidth ?? '100%',
                    minH: '44px',
                    opacity: isDisabled ? 0.9 : 1,
                    backgroundColor: '#FFF',
                    border: error ? '2px solid red' : '',
                    borderColor: colors.border,
                  }),
                  menuList: provided => ({
                    ...provided,
                    zIndex: 9999,
                    bg: 'white',
                    maxH: '150px',
                    borderColor: colors.border,
                    borderRadius: 0,
                    boxShadow: '0px 2px 4px #1E293B0D',
                  }),
                  multiValue: provided => ({
                    ...provided,
                    backgroundColor: '#EBF5FF',
                    borderRadius: '4px',
                  }),
                  multiValueLabel: provided => ({
                    ...provided,
                    color: '#1E40AF',
                    fontSize: '12px',
                  }),
                  multiValueRemove: provided => ({
                    ...provided,
                    color: '#1E40AF',
                    '&:hover': {
                      backgroundColor: '#DBEAFE',
                      color: '#1E3A8A',
                    },
                  }),
                  dropdownIndicator: provided => ({
                    ...provided,
                    bg: 'transparent',
                    p: 0,
                    w: 3,
                    mx: 2,
                    cursor: 'inherit',
                  }),
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                }}
                {...rest}
              />
              {selectedCount > 0 && (
                <Text fontSize="xs" color={isMaxReached ? 'orange.500' : 'gray.500'} mt={1}>
                  {selectedCount} de {maxSelectedOptions} selecionados
                  {isMaxReached && ' (limite atingido)'}
                </Text>
              )}
            </>
          );
        }}
      />

      {errors && <FormErrorMessage>{extractErrorMessage(error?.message)}</FormErrorMessage>}
    </FormControl>
  );
}

export default MultiSelectInputForm;
